/**
 * GraphQL Operations for Organizations (Clients)
 * Queries and mutations for the Clients module
 */

import { gql } from '@apollo/client';

// =============================================================================
// Fragments
// =============================================================================

/**
 * Contact fields fragment
 */
export const CONTACT_FIELDS = gql`
  fragment ContactFields on Contact {
    id
    organizationId
    firstName
    lastName
    email
    phone
    title
    type
    types
    createdAt
    updatedAt
  }
`;

/**
 * Address fields fragment
 */
export const ADDRESS_FIELDS = gql`
  fragment AddressFields on Address {
    id
    organizationId
    type
    street1
    street2
    city
    state
    zipCode
    country
    createdAt
    updatedAt
  }
`;

/**
 * Organization fields fragment (includes contacts and addresses)
 */
export const ORGANIZATION_FIELDS = gql`
  fragment OrganizationFields on Organization {
    id
    name
    type
    status
    website
    phone
    blastTier
    blastFeatures {
      cms
      docPrep
      sba
      docManagement
      tasksWorkflows
      portfolioMonitoring
      borrowerPortal
    }
    blastSyncStatus
    blastSyncedAt
    blastSyncError
    createdAt
    updatedAt
    primaryContact {
      ...ContactFields
    }
    contacts {
      ...ContactFields
    }
    addresses {
      ...AddressFields
    }
  }
  ${CONTACT_FIELDS}
  ${ADDRESS_FIELDS}
`;

// =============================================================================
// Queries
// =============================================================================

/**
 * Get paginated organizations with optional filters and search
 */
export const GET_ORGANIZATIONS = gql`
  query GetOrganizations(
    $type: OrganizationType
    $status: OrganizationStatus
    $search: String
    $limit: Int
    $offset: Int
  ) {
    organizations(
      type: $type
      status: $status
      search: $search
      limit: $limit
      offset: $offset
    ) {
      items {
        id
        name
        type
        status
        website
        phone
        blastTier
        blastSyncStatus
        createdAt
        updatedAt
        primaryContact {
          id
          firstName
          lastName
          email
          phone
          title
          type
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

/**
 * Get a single organization by ID with full details
 */
export const GET_ORGANIZATION = gql`
  query GetOrganization($id: ID!) {
    organization(id: $id) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

// =============================================================================
// Mutations
// =============================================================================

/**
 * Create a new organization
 */
export const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
      name
      type
      status
      website
      phone
      blastTier
      blastSyncStatus
      createdAt
      updatedAt
    }
  }
`;

/**
 * Update an existing organization
 */
export const UPDATE_ORGANIZATION = gql`
  mutation UpdateOrganization($id: ID!, $input: UpdateOrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

/**
 * Delete an organization (soft delete)
 */
export const DELETE_ORGANIZATION = gql`
  mutation DeleteOrganization($id: ID!) {
    deleteOrganization(id: $id)
  }
`;

/**
 * Update BLAST tier for an organization
 */
export const UPDATE_BLAST_TIER = gql`
  mutation UpdateBlastTier($organizationId: ID!, $input: UpdateBlastTierInput!) {
    updateBlastTier(organizationId: $organizationId, input: $input) {
      id
      blastTier
      blastSyncStatus
    }
  }
`;

/**
 * Update BLAST feature entitlements (7-key contract). Slope is the
 * entitlement system of record — this writes directly to BLAST; BLAST
 * applies the change at next login / within its ~15-minute cache.
 */
export const UPDATE_BLAST_FEATURES = gql`
  mutation UpdateBlastFeatures($organizationId: ID!, $input: UpdateBlastFeaturesInput!) {
    updateBlastFeatures(organizationId: $organizationId, input: $input) {
      id
      blastFeatures {
        cms
        docPrep
        sba
        docManagement
        tasksWorkflows
        portfolioMonitoring
        borrowerPortal
      }
      blastSyncStatus
    }
  }
`;

/**
 * Sync organization to BLAST
 */
export const SYNC_ORGANIZATION_TO_BLAST = gql`
  mutation SyncOrganizationToBlast($id: ID!) {
    syncOrganizationToBlast(id: $id) {
      id
      blastSyncStatus
      blastSyncedAt
      blastSyncError
    }
  }
`;

/**
 * Create a new contact for an organization
 */
export const CREATE_CONTACT = gql`
  mutation CreateContact($input: CreateContactInput!) {
    createContact(input: $input) {
      ...ContactFields
    }
  }
  ${CONTACT_FIELDS}
`;

/**
 * Create a new address for an organization
 */
export const CREATE_ADDRESS = gql`
  mutation CreateAddress($input: CreateAddressInput!) {
    createAddress(input: $input) {
      ...AddressFields
    }
  }
  ${ADDRESS_FIELDS}
`;

/**
 * Update an existing contact
 */
export const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      ...ContactFields
    }
  }
  ${CONTACT_FIELDS}
`;

/**
 * Delete a contact
 */
export const DELETE_CONTACT = gql`
  mutation DeleteContact($id: ID!) {
    deleteContact(id: $id)
  }
`;

// =============================================================================
// TypeScript Types (for use with Apollo hooks)
// =============================================================================

// Contact type enum - all available contact roles
export type ContactTypeEnum = 'PRIMARY' | 'BILLING' | 'EXECUTIVE' | 'TECHNICAL' | 'SUPPORT' | 'DECISION_MAKER';

export interface Contact {
  id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  title?: string | null;
  /** @deprecated Use types array instead */
  type: ContactTypeEnum;
  /** Array of contact roles (multi-select) */
  types?: ContactTypeEnum[];
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  id: string;
  organizationId: string;
  type: 'HEADQUARTERS' | 'BILLING' | 'MAILING' | 'BRANCH';
  street1: string;
  street2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

export interface Organization {
  id: string;
  name: string;
  type: 'CFI' | 'PSP';
  status: 'PROSPECT' | 'ACTIVE' | 'INACTIVE';
  website?: string | null;
  phone?: string | null;
  blastTier: 'ZERO' | 'LITE' | 'FULL';
  /** The v1 BLAST feature entitlement contract (7 keys) */
  blastFeatures: {
    cms: boolean;
    docPrep: boolean;
    sba: boolean;
    docManagement: boolean;
    tasksWorkflows: boolean;
    portfolioMonitoring: boolean;
    borrowerPortal: boolean;
  };
  blastSyncStatus: 'PENDING' | 'SYNCED' | 'ERROR';
  blastSyncedAt?: string | null;
  blastSyncError?: string | null;
  primaryContact?: Contact | null;
  contacts: Contact[];
  addresses: Address[];
  createdAt: string;
  updatedAt: string;
}

// Pagination types
export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export interface OrganizationConnection {
  items: Organization[];
  pageInfo: PageInfo;
}

// Query response types
export interface GetOrganizationsData {
  organizations: OrganizationConnection;
}

export interface GetOrganizationsVars {
  type?: 'CFI' | 'PSP';
  status?: 'PROSPECT' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  limit?: number;
  offset?: number;
}

export interface GetOrganizationData {
  organization: Organization | null;
}

export interface GetOrganizationVars {
  id: string;
}

// Mutation input types
export interface CreateOrganizationInput {
  name: string;
  type: 'CFI' | 'PSP';
  status?: 'PROSPECT' | 'ACTIVE' | 'INACTIVE';
  website?: string;
  phone?: string;
}

export interface UpdateOrganizationInput {
  name?: string;
  type?: 'CFI' | 'PSP';
  status?: 'PROSPECT' | 'ACTIVE' | 'INACTIVE';
  website?: string;
  phone?: string;
  blastTier?: 'ZERO' | 'LITE' | 'FULL';
}

export interface CreateContactInput {
  organizationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  /** Array of contact types (required, at least one) */
  types: ContactTypeEnum[];
  /** @deprecated Use types array instead */
  type?: ContactTypeEnum;
}

export interface UpdateContactInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  title?: string;
  /** Array of contact types */
  types?: ContactTypeEnum[];
  /** @deprecated Use types array instead */
  type?: ContactTypeEnum;
}

export interface CreateAddressInput {
  organizationId: string;
  type: 'HEADQUARTERS' | 'BILLING' | 'MAILING' | 'BRANCH';
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
}

// Mutation response types
export interface CreateOrganizationData {
  createOrganization: Organization;
}

export interface UpdateOrganizationData {
  updateOrganization: Organization;
}

export interface DeleteOrganizationData {
  deleteOrganization: boolean;
}
